import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import {
  LuSearch,
  LuMapPin,
  LuClock,
  LuNewspaper,
  LuSparkles,
  LuFileText,
  LuCopy,
  LuExternalLink,
  LuLock,
  LuX,
  LuZap,
  LuUpload,
  LuCheck,
  LuInfo,
  LuListChecks,
  LuGraduationCap,
  LuRefreshCw,
  LuBriefcase,
  LuCircleCheck,
  LuSlidersHorizontal,
  LuBookmark,
  LuBookmarkCheck,
  LuBuilding2,
  LuBanknote,
  LuHouse,
  LuTrendingUp,
  LuChevronDown,
  LuArrowLeft,
} from "react-icons/lu";
import { Btn, Card, Badge, Spinner, Skel, Note, MatchRing, Sheet, Select } from "../components/Ui";
import { scoreJob, jobFacts } from "../matchScore";
import CustomResumeSheet from "./CustomResumeSheet";
import {
  setJobsList,
  setFileResume,
  setShowPro,
  setProfileData,
  setJobResumeUpload,
  setProfileUpdateStatus,
} from "../../store/actions/actions";
import "./jobs.css";

/* One score per job (Pavithra Jobs #3): the list ring is a fast client-side
   estimate (scoreJob) while "Analyze match" is the authoritative backend AI
   score — they differed for the same job. Once a job is analyzed we cache its
   real AI match % here and the list ring shows THAT instead of the estimate. */
const analyzedScores = new Map();

/* ---------------------------------------------------------------
   Legacy endpoints — do not change URLs, payloads or headers.
   --------------------------------------------------------------- */
const JOBS_STREAM_API =
  "https://us-east1-foursssolutions.cloudfunctions.net/Get_Jobs_From_Database_Comparing_Resume_streaming_v2";
const FILE_API = "https://us-east1-foursssolutions.cloudfunctions.net/send_resume_details_front_end_v2";
const APPLY_CHECK_API = "https://us-east1-foursssolutions.cloudfunctions.net/check_apply_reapply_buttons_v2";
const RESUME_SUGGESTIONS_STREAM_API = "https://resume-update-suggestions-all-v4-980069659423.us-east1.run.app";
export const TAILORED_SUGGESTIONS_STREAM_API =
  "https://resume-update-suggestions-tailored-job-v4-980069659423.us-east1.run.app";
const PAYMENT_API = "https://us-east1-foursssolutions.cloudfunctions.net/create_payment_request_v2";
const INTERNAL_APPLY_API = "https://job-application-submission-internal-v10-737421501165.us-east1.run.app";
const RESUME_BUCKET_API =
  "https://us-east1-foursssolutions.cloudfunctions.net/Resume_upload_bucket_store_in_table_v2";

/* ---------------------------------------------------------------
   Small helpers (formatting only — no API behavior)
   --------------------------------------------------------------- */
export function timeAgo(postedAt) {
  const now = new Date();
  const postedDate = new Date(postedAt);
  const seconds = Math.floor((now - postedDate) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  return "Just now";
}

export function isEarlyApplicant(postedDate) {
  const diffHours = Math.abs(new Date() - new Date(postedDate)) / (1000 * 60 * 60);
  return diffHours <= 48;
}

function companyInitials(name) {
  return (name || "?").substring(0, 2).toUpperCase();
}

/* Feeds shout their employment types ("FULLTIME") — the cards don't. */
function titleCase(s) {
  const t = String(s || "").replace(/_/g, " ").trim();
  if (!t) return "";
  return /^FULL ?TIME$/i.test(t)
    ? "Full-time"
    : /^PART ?TIME$/i.test(t)
    ? "Part-time"
    : t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

/* A few upstream feeds deliver a text field as a structured object ({ text },
   { value }, a locale map) where the rest send a plain string. React throws on
   an object child, so a single such record used to take the whole list down
   with it (error #31 -> ErrorBoundary) instead of just spoiling its own card.
   Pull out the text the card is meant to show; fall back to empty rather than
   printing "[object Object]". */
const TEXT_KEYS = ["text", "value", "content", "description", "name", "label", "en", "en_US"];

function asDisplayText(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(asDisplayText).filter(Boolean).join("\n");
  if (typeof v === "object") {
    for (const k of TEXT_KEYS) {
      if (typeof v[k] === "string" && v[k].trim()) return v[k];
    }
    /* no recognised text key: keep the string leaves and drop the rest — a
       partial description still reads, an object child crashes the page */
    const leaves = Object.values(v).filter((x) => typeof x === "string" && x.trim());
    if (leaves.length) return leaves.join("\n");
    for (const nested of Object.values(v)) {
      if (nested && typeof nested === "object") {
        const t = asDisplayText(nested);
        if (t) return t;
      }
    }
  }
  return "";
}

/* Every field the cards and the detail panel print as a React child (or hand to
   an href). Booleans the UI branches on — job_is_remote — stay untouched. */
const DISPLAY_FIELDS = [
  "job_title",
  "employer_name",
  "job_description",
  "job_city",
  "job_state",
  "job_country",
  "job_employment_type",
  "job_publisher",
  "salary_range",
  "job_apply_link",
  "employer_website",
];

export function normalizeJobRecord(job) {
  if (!job || typeof job !== "object") return job;
  let changed = false;
  const out = { ...job };
  for (const f of DISPLAY_FIELDS) {
    if (out[f] != null && typeof out[f] !== "string") {
      out[f] = asDisplayText(out[f]);
      changed = true;
    }
  }
  /* The highlight lists reach SugList, which stringifies — an object entry
     survives as a literal "[object Object]" bullet rather than a crash. Same
     origin, same treatment. */
  const h = out.job_highlights;
  if (h && typeof h === "object" && !Array.isArray(h)) {
    const fixed = {};
    let listChanged = false;
    for (const [k, v] of Object.entries(h)) {
      if (Array.isArray(v) && v.some((x) => x !== null && typeof x === "object")) {
        fixed[k] = v.map(asDisplayText).filter(Boolean);
        listChanged = true;
      } else {
        fixed[k] = v;
      }
    }
    if (listChanged) {
      out.job_highlights = fixed;
      changed = true;
    }
  }
  return changed ? out : job;
}

/* The posting as one plain-text blob for the resume services. Highlights are
   folded in when the source provides them (lever, smartrecruiters) and simply
   skipped when it doesn't, so every source yields usable text. */
export function jobInfoText(job) {
  if (!job) return "";
  const h = job.job_highlights || {};
  const lists = ["Responsibilities", "Qualifications", "Benefits"]
    .map((k) => (Array.isArray(h[k]) && h[k].length ? `${k}:\n${h[k].join("\n")}` : ""))
    .filter(Boolean);
  return [
    job.job_title,
    job.employer_name ? `at ${job.employer_name}` : "",
    typeof job.job_description === "string" ? job.job_description : "",
    ...lists,
  ]
    .filter(Boolean)
    .join("\n\n")
    .trim();
}



/* Newline-delimited JSON stream reader — identical read loop to the
   legacy streamResponseChunks (data: prefix strip, buffer carry,
   final-buffer flush). */
export async function streamJsonLines(response, onChunk) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let done = false;
  let buffer = "";

  while (!done) {
    const { value, done: doneReading } = await reader.read();
    done = doneReading;
    if (value) {
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();
      for (const line of lines) {
        const trimmedLine = line.startsWith("data:") ? line.slice(5).trim() : line.trim();
        if (trimmedLine) {
          try {
            onChunk(JSON.parse(trimmedLine));
          } catch (e) {
            console.error("Error parsing chunk", e, trimmedLine);
          }
        }
      }
    }
  }
  if (buffer) {
    const trimmedLine = buffer.startsWith("data:") ? buffer.slice(5).trim() : buffer.trim();
    if (trimmedLine) {
      try {
        onChunk(JSON.parse(trimmedLine));
      } catch (e) {
        console.error("Error parsing final chunk", e, trimmedLine);
      }
    }
  }
}

/* Legacy clipboard formatting from TailoredJobsdata handleCopy —
   now returns the promise so callers can flash inline feedback. */
function copyChunkData(jsonData) {
  const formatData = (data, indent = "") => {
    if (Array.isArray(data)) {
      return data
        .map((item) => (typeof item === "object" ? formatData(item, `${indent}  `) : `${indent}- ${item}`))
        .join("\n");
    }
    if (typeof data === "object" && data !== null) {
      return Object.entries(data)
        .map(([key, value]) => `${indent}${key.replace(/_/g, " ")}: \n${formatData(value, `${indent}  `)}`)
        .join("\n");
    }
    return `${indent}- ${String(data)}`;
  };
  return navigator.clipboard.writeText(formatData(jsonData)).catch((err) => {
    toast.error("Could not copy to clipboard");
    throw err;
  });
}

/* ---------------------------------------------------------------
   Saved jobs — purely client-side (localStorage), mirroring the
   Lovable use-persistent-set hook. No backend involved.
   --------------------------------------------------------------- */
const SAVED_JOBS_KEY = "csx_saved_jobs";

export function useSavedJobs() {
  const [savedIds, setSavedIds] = useState(() => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(SAVED_JOBS_KEY) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  });
  const toggleSaved = (id) => {
    if (!id) return;
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        window.localStorage.setItem(SAVED_JOBS_KEY, JSON.stringify(next));
      } catch (err) {
        console.warn("Could not persist saved jobs", err);
      }
      return next;
    });
  };
  return { savedIds, toggleSaved };
}

/* ---------------------------------------------------------------
   Upgrade prompt (replaces the legacy pro-bg modals). Links to
   /subscription via setShowPro exactly like legacy makePayment.
   --------------------------------------------------------------- */
const UPGRADE_COPY = {
  filters: "Upgrade to Pro to access these features and reach your career goals faster with the help of CareerSavvy!",
  tailored:
    "Upgrade to Pro to unlock the Analyze Match feature and accelerate your job search with CareerSavvy! Learn more about Pro features:",
  genresume:
    "Upgrade to Pro to unlock AI Resume Insights and Generate Custom Resume for your dream job! Learn more about Pro features:",
};

export function UpgradeSheet({ open, onClose, text, video }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const goPro = () => {
    onClose();
    dispatch(setShowPro(true));
    navigate("/subscription");
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Upgrade to Pro"
      footer={
        <>
          <Btn variant="ghost" className="btn-pill" onClick={onClose}>
            Not now
          </Btn>
          <Btn className="btn-pill btn-mint" onClick={goPro}>
            Upgrade now
          </Btn>
        </>
      }
    >
      <div className="stack-3">
        <Note tone="accent">
          <LuSparkles size={16} />
          <span>{text || UPGRADE_COPY.filters}</span>
        </Note>
        {video ? (
          <iframe
            className="upgrade-video"
            src="https://www.youtube.com/embed/Nkl5TiWo6Bs?si=Ued5SnlPG40K6uSf"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : null}
      </div>
    </Sheet>
  );
}

/* ---------------------------------------------------------------
   Streamed suggestion chunks (assessment / domain_match /
   experience_enhancement / skill_gaps / certifications /
   action_items) — same chunk_type contract as TailoredJobsdata,
   rendered with the Lovable analyze-sheet anatomy.
   --------------------------------------------------------------- */
function SugList({ items, dim }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="dot-list">
      {items.map((item, i) => (
        <li key={i}>
          <span className={`dot${dim ? " dim" : ""}`} />
          <span>{decodeEntities(item)}</span>
        </li>
      ))}
    </ul>
  );
}

function ChipRow({ items, on }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="chip-wrap">
      {items.map((s, i) => (
        <span key={`${s}-${i}`} className={on ? "ld-chip-on" : "ld-chip-off"}>
          {s}
        </span>
      ))}
    </div>
  );
}

function CopyBtn({ data, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="copy-btn"
      aria-label={`Copy ${label || "section"}`}
      onClick={() =>
        copyChunkData(data)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {})
      }
    >
      {copied ? <LuCheck size={13} className="ok" /> : <LuCopy size={13} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function SuggestionChunks({ updates }) {
  const assessment = updates.find((c) => c.chunk_type === "assessment");
  const matchPercentage = assessment?.data?.match_percentage || 0;
  const verdict = assessment?.data?.submission_verdict || "";
  const verdictReason = assessment?.data?.verdict_reason || "";

  return (
    <div className="stack-3">
      {assessment ? (
        <div className="score-card">
          <div className="row" style={{ gap: 16, alignItems: "center" }}>
            <MatchRing score={matchPercentage} size={56} />
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="ld-eyebrow">Resume match</div>
              <div className="ld-stat-n text-mint" style={{ fontSize: 32, lineHeight: 1.15 }}>
                {matchPercentage}%
              </div>
            </div>
          </div>
          {verdict ? (
            <div className="t-sm t-ink mt-2" style={{ fontWeight: 600 }}>
              {String(verdict).replace(/_/g, " ")}
            </div>
          ) : null}
          {verdictReason ? <p className="t-sm mt-1">{verdictReason}</p> : null}
        </div>
      ) : null}

      {updates.map((chunk, index) => {
        switch (chunk.chunk_type) {
          case "domain_match":
            return (
              <section className="chunk-card" key={`dm-${index}`}>
                <div className="chunk-head">
                  <span className="chunk-title">
                    <LuInfo size={15} /> Domain match
                  </span>
                </div>
                <div className="stack-2">
                  {chunk.data.jd_domain?.length > 0 ? (
                    <div className="t-sm">
                      <strong>Job domain:</strong> {chunk.data.jd_domain}
                    </div>
                  ) : null}
                  {chunk.data.resume_domain?.length > 0 ? (
                    <div className="t-sm">
                      <strong>Resume domain:</strong> {chunk.data.resume_domain}
                    </div>
                  ) : null}
                  {chunk.data.match_level?.length > 0 ? (
                    <div className="t-sm">
                      <strong>Match level:</strong> {chunk.data.match_level}
                    </div>
                  ) : null}
                  {chunk.data.transferable_skills?.length > 0 ? (
                    <>
                      <div className="sug-sub">Transferable skills</div>
                      <SugList items={chunk.data.transferable_skills} />
                    </>
                  ) : null}
                  {chunk.data.gap_analysis?.length > 0 ? (
                    <>
                      <div className="sug-sub">Gap analysis</div>
                      <SugList items={chunk.data.gap_analysis} dim />
                    </>
                  ) : null}
                </div>
              </section>
            );

          case "experience_enhancement":
            return (
              <React.Fragment key={`exp-${index}`}>
                {(chunk.data || []).map((exp, expIndex) => (
                  <section className="chunk-card" key={`exp-${index}-${expIndex}`}>
                    <div className="chunk-head">
                      <span className="chunk-title">
                        <LuBriefcase size={15} /> {exp.company}
                        {exp.period ? <span className="t-xs">· {exp.period}</span> : null}
                      </span>
                      <CopyBtn data={chunk.data} label="experience enhancements" />
                    </div>
                    <div className="sug-sub">Suggested points</div>
                    <SugList items={(exp.suggested_points || []).map((p) => p.point)} />
                    <div className="sug-sub">Why important</div>
                    <SugList items={(exp.suggested_points || []).map((p) => p.why_important)} dim />
                    <div className="sug-sub">Success indicators to add</div>
                    <SugList items={(exp.suggested_points || []).map((p) => p.metrics_to_add)} dim />
                  </section>
                ))}
              </React.Fragment>
            );

          case "skill_gaps":
            return (
              <section className="chunk-card" key={`sg-${index}`}>
                <div className="chunk-head">
                  <span className="chunk-title">
                    <LuListChecks size={15} /> Skill gaps
                  </span>
                  <CopyBtn data={chunk.data} label="skill gaps" />
                </div>
                {chunk.data.critical_missing?.length > 0 ? (
                  <>
                    <div className="sug-sub">Critical missing</div>
                    <ChipRow items={chunk.data.critical_missing} />
                  </>
                ) : null}
                {chunk.data.recommended_missing?.length > 0 ? (
                  <>
                    <div className="sug-sub">Recommended missing</div>
                    <ChipRow items={chunk.data.recommended_missing} />
                  </>
                ) : null}
                {Array.isArray(chunk.data.training_suggestions) && chunk.data.training_suggestions.length > 0 ? (
                  <>
                    <div className="sug-sub">Training suggestions</div>
                    <SugList items={chunk.data.training_suggestions} />
                  </>
                ) : null}
              </section>
            );

          case "certifications":
            return (
              <section className="chunk-card" key={`cert-${index}`}>
                <div className="chunk-head">
                  <span className="chunk-title">
                    <LuGraduationCap size={15} /> Certifications
                  </span>
                  <CopyBtn data={chunk.data} label="certifications" />
                </div>
                {chunk.data.required?.length > 0 ? (
                  <>
                    <div className="sug-sub">Required</div>
                    <ChipRow items={chunk.data.required} on />
                  </>
                ) : null}
                {chunk.data.missing?.length > 0 ? (
                  <>
                    <div className="sug-sub">Missing</div>
                    <ChipRow items={chunk.data.missing} />
                  </>
                ) : null}
              </section>
            );

          case "action_items":
            return (
              <section className="chunk-card" key={`ai-${index}`}>
                <div className="chunk-head">
                  <span className="chunk-title">
                    <LuZap size={15} /> Action items
                  </span>
                  <CopyBtn data={chunk.data} label="action items" />
                </div>
                {chunk.data.immediate?.length > 0 ? (
                  <>
                    <div className="sug-sub">Immediate term</div>
                    <SugList items={chunk.data.immediate} />
                  </>
                ) : null}
                {chunk.data.short_term?.length > 0 ? (
                  <>
                    <div className="sug-sub">Short term</div>
                    <SugList items={chunk.data.short_term} />
                  </>
                ) : null}
                {chunk.data.long_term?.length > 0 ? (
                  <>
                    <div className="sug-sub">Long term</div>
                    <SugList items={chunk.data.long_term} dim />
                  </>
                ) : null}
              </section>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}

/* ---------------------------------------------------------------
   Match analysis sheet (Lovable analyze-sheet skin) — streams the
   same Cloud Run endpoint the legacy resumeToggle/getResumeUpdates
   flow used, then Generate Custom Resume via
   create_pdf_from_resume_suggestions_v2.
   --------------------------------------------------------------- */
export function ResumeInsightsSheet({ open, onClose, job, autoStudio = false }) {
  const userEmail = useSelector((s) => s.auth.auth.email);
  const { apiToken, fileResume, featuresToBlock, resumeJson } = useSelector((s) => s.profile);
  const isGenResumeBlocked = featuresToBlock.includes("block_genresume");

  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const aliveRef = useRef(false);
  const updatesRef = useRef([]);

  useEffect(() => {
    if (!open || !job?.job_id) return undefined;
    aliveRef.current = true;
    updatesRef.current = [];
    setUpdates([]);
    setError(null);
    setLoading(true);

    const run = async () => {
      try {
        if (!userEmail) {
          console.error("User email is not available.");
          return;
        }
        /* Send the posting text alongside job_id. The service builds its GPT
           input from job_highlights.Qualifications/Responsibilities when it
           only gets a job_id — a shape ATS feeds don't populate (greenhouse
           and ashby carry none at all), so those lookups 404 with "Job info
           not found". Passing job_description takes the service's direct
           branch and works for every source. */
        const queryObj = {
          email_id: userEmail,
          file_name: fileResume,
          job_id: job.job_id,
          job_description: jobInfoText(job),
        };
        const response = await fetch(RESUME_SUGGESTIONS_STREAM_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
          body: JSON.stringify(queryObj),
        });
        if (!response.ok) throw new Error("Failed to fetch data");
        await streamJsonLines(response, (chunk) => {
          if (!aliveRef.current) return;
          updatesRef.current = [...updatesRef.current, chunk];
          setUpdates(updatesRef.current);
        });
        if (aliveRef.current) setLoading(false);
      } catch (err) {
        if (aliveRef.current) {
          setError(err.message);
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      aliveRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, job?.job_id]);

  /* entered via "Tailor resume": skip the reading step and go straight to
     the wizard the moment the analysis has something to work with */
  useEffect(() => {
    if (autoStudio && open && !loading && updates.length > 0 && !isGenResumeBlocked) {
      setStudioOpen(true);
    }
  }, [autoStudio, open, loading, updates.length, isGenResumeBlocked]);

  /* cache this job's authoritative AI match % so the list ring matches Analyze
     (Pavithra Jobs #3 — no more two different numbers for the same job) */
  useEffect(() => {
    const assessment = updates.find((c) => c.chunk_type === "assessment");
    const pct = assessment?.data?.match_percentage;
    if (job?.job_id && typeof pct === "number" && pct > 0) {
      analyzedScores.set(job.job_id, pct);
    }
  }, [updates, job?.job_id]);

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        title="Match analysis"
        width="min(576px, 100vw)"
        footer={
          updates.length > 0 && !loading ? (
            <Btn
              full
              className="btn-pill btn-mint"
              onClick={() => (isGenResumeBlocked ? setUpgradeOpen(true) : setStudioOpen(true))}
            >
              <LuFileText size={15} /> Tailor my resume for this job
            </Btn>
          ) : null
        }
      >
        {job ? (
          <p className="t-sm mb-2">
            {job.job_title} at {job.employer_name}
          </p>
        ) : null}

        {error ? (
          <Note tone="danger">
            <LuInfo size={16} />
            <span>Could not load resume insights: {error}</span>
          </Note>
        ) : null}

        {loading && updates.length === 0 ? (
          <div className="stack-3">
            <div className="row" style={{ gap: 12 }}>
              <Spinner lg />
              <div className="t-sm">
                Almost there! Our AI is reviewing your resume and the job description to highlight key skills and
                experience.
              </div>
            </div>
            <Skel h={92} r={18} />
            <Skel h={110} r={18} />
            <Skel h={110} r={18} />
          </div>
        ) : (
          <SuggestionChunks updates={updates} />
        )}

        {loading && updates.length > 0 ? (
          <div className="stream-row">
            <Spinner /> Reviewing your resume…
          </div>
        ) : null}

      </Sheet>
      <UpgradeSheet open={upgradeOpen} onClose={() => setUpgradeOpen(false)} text={UPGRADE_COPY.genresume} video />
      <CustomResumeSheet
        open={studioOpen}
        onClose={() => setStudioOpen(false)}
        job={job}
        updates={updates}
        resumeJson={resumeJson}
      />
    </>
  );
}

/* ---------------------------------------------------------------
   Tailored Job Search (paste a JD, Analyze Match) — legacy
   tailoredJobSearch.jsx flow, gated by block_tailored.
   --------------------------------------------------------------- */
export function TailoredMatchSheet({ open, onClose }) {
  const userEmail = useSelector((s) => s.auth.auth.email);
  const { apiToken, featuresToBlock } = useSelector((s) => s.profile);
  const isTailoredJobsBlocked = featuresToBlock.includes("block_tailored");

  const [jobDescription, setJobDescription] = useState("");
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const updatesRef = useRef([]);

  const analyze = async () => {
    if (isTailoredJobsBlocked) {
      setUpgradeOpen(true);
      return;
    }
    updatesRef.current = [];
    setUpdates([]);
    setError(null);
    setLoading(true);
    try {
      if (!userEmail) {
        console.error("User email is not available.");
        return;
      }
      const queryObj = {
        email_id: userEmail,
        job_description: jobDescription,
      };
      const response = await fetch(TAILORED_SUGGESTIONS_STREAM_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(queryObj),
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      await streamJsonLines(response, (chunk) => {
        updatesRef.current = [...updatesRef.current, chunk];
        setUpdates(updatesRef.current);
      });
      setReady(true);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const reset = () => {
    setReady(false);
    setUpdates([]);
    updatesRef.current = [];
    setError(null);
  };

  return (
    <>
      <Sheet
        open={open}
        onClose={() => {
          reset();
          onClose();
        }}
        title="Tailored job search"
        width="min(576px, 100vw)"
        footer={
          !ready ? (
            <Btn
              full
              className="btn-pill btn-mint"
              onClick={analyze}
              loading={loading}
              disabled={loading || jobDescription.trim() === ""}
            >
              <LuSparkles size={15} /> Analyze match
            </Btn>
          ) : (
            <Btn variant="ghost" className="btn-pill" onClick={reset}>
              <LuRefreshCw size={15} /> Analyze another description
            </Btn>
          )
        }
      >
        {error ? (
          <Note tone="danger">
            <LuInfo size={16} />
            <span>Analyze failed: {error}</span>
          </Note>
        ) : null}

        {!ready ? (
          <div className="stack-3">
            <Note tone="info">
              <LuInfo size={16} />
              <span>
                Paste a job description to see how your resume aligns with the specified requirements and receive
                personalized job matching suggestions.
              </span>
            </Note>
            <textarea
              className="input"
              rows={12}
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the job description here to see how well your resume fits..."
            />
            {loading ? (
              <div className="stream-row">
                <Spinner /> Analyzing your resume against this description…
              </div>
            ) : null}
          </div>
        ) : (
          <SuggestionChunks updates={updates} />
        )}
      </Sheet>
      <UpgradeSheet open={upgradeOpen} onClose={() => setUpgradeOpen(false)} text={UPGRADE_COPY.tailored} video />
    </>
  );
}

/* ---------------------------------------------------------------
   Auto Apply — subscription upsell + create_payment_request_v2
   (product_code PC_102), same as legacy buySubscription.
   --------------------------------------------------------------- */
export function AutoApplySheet({ open, onClose }) {
  const userEmail = useSelector((s) => s.auth.auth.email);
  const { apiToken } = useSelector((s) => s.profile);
  const [loading, setLoading] = useState(false);

  const buySubscription = async () => {
    setLoading(true);
    try {
      if (!userEmail) {
        console.error("User email is not available.");
        return;
      }
      const paymentPayload = {
        user_email: userEmail,
        product_code: "PC_102",
      };
      const response = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(paymentPayload),
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      window.location.href = `${data.payment_link}`;
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toast.error("Could not start the subscription checkout. Please try again.");
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Auto Apply" width="min(520px, 100vw)">
      <div className="stack-3">
        <p className="t-sm">
          Let CareerSavvy handle job applications for you effortlessly. Focus your time on preparing for interviews
          while we apply to relevant positions on your behalf.
        </p>
        <ul className="sug-list">
          <li>
            <LuCheck size={14} />
            <span>CareerSavvy identifies jobs tailored to your resume.</span>
          </li>
          <li>
            <LuCheck size={14} />
            <span>CareerSavvy matches jobs to your skills and experience.</span>
          </li>
          <li>
            <LuCheck size={14} />
            <span>CareerSavvy connects you with roles that align with your profile.</span>
          </li>
        </ul>
        <div className="plan-card">
          <div className="t-label">Auto Apply subscription plan</div>
          <div className="row mt-1" style={{ alignItems: "baseline", gap: 6 }}>
            <span className="plan-price">$49.99</span>
            <span className="t-xs">USD / month</span>
          </div>
          <p className="t-sm mt-2" style={{ fontWeight: 600 }}>
            Everything in one plan (includes all Pro features)
          </p>
          <ul className="sug-list mt-2">
            <li>
              <LuCheck size={14} />
              <span>Apply to up to 50 jobs monthly.</span>
            </li>
            <li>
              <LuCheck size={14} />
              <span>Receive detailed job application reports.</span>
            </li>
            <li>
              <LuCheck size={14} />
              <span>Track visibility into all positions applied to.</span>
            </li>
          </ul>
          <Btn full className="mt-3 btn-pill btn-mint" onClick={buySubscription} loading={loading} disabled={loading}>
            Subscribe now
          </Btn>
        </div>
      </div>
    </Sheet>
  );
}

/* ---------------------------------------------------------------
   Easy Apply (internal jobs) — same call sequence as the legacy
   jobApply.jsx modal: profile prefill + optional update via
   send_resume_details_front_end_v2, then the internal submission
   service (multipart when a new resume is chosen, JSON otherwise).
   --------------------------------------------------------------- */
const normalizeToE164 = (country, input) => {
  const d = String(input || "").replace(/\D+/g, "");
  if (country === "USA") {
    const nat = d.startsWith("1") ? d.slice(1) : d;
    return nat ? `+1${nat.slice(0, 10)}` : "";
  }
  if (country === "India") {
    const nat = d.startsWith("91") ? d.slice(2) : d;
    return nat ? `+91${nat.slice(0, 10)}` : "";
  }
  return d ? `+${d}` : "";
};
const nationalDigits = (country, e164) => {
  const d = String(e164 || "").replace(/\D+/g, "");
  if (country === "USA") return d.replace(/^1/, "").slice(0, 10);
  if (country === "India") return d.replace(/^91/, "").slice(0, 10);
  return d;
};
const isValidPhone = (country, e164) => {
  if (!country) return false;
  const nat = nationalDigits(country, e164);
  if (country === "USA" || country === "India") return nat.length === 10;
  return nat.length > 0;
};

export function EasyApplySheet({ open, onClose, job }) {
  const dispatch = useDispatch();
  const userEmail = useSelector((s) => s.auth.auth.email);
  const { profileData, apiToken, jobResumeUpload } = useSelector((s) => s.profile);

  const [fileName, setFileName] = useState("");
  const [chosenFiles, setChosenFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [fetchedProfileData, setFetchedProfileData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setApplicationStatus("");
    setMessage("");
    setErrors({});
    setFile(null);
    setChosenFiles([]);
    if (userEmail) fetchFilenames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (applicationStatus !== "") {
      const t = setTimeout(() => {
        setApplicationStatus("");
        onClose();
      }, 3000);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationStatus]);

  const fetchFilenames = async () => {
    try {
      const queryObj = { emailid: userEmail };
      const response = await fetch(FILE_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(queryObj),
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      if (data.file_details && data.file_details.length > 0) {
        const extractedFilenames = data.file_details.map((f) => f.file_name);
        setFileName(extractedFilenames[0] || "");
      }
      if (data.user_details && data.user_details.length > 0) {
        const extractedProfileData = data.user_details[0];
        dispatch(setProfileData(extractedProfileData));
        setFetchedProfileData(extractedProfileData);
      }
    } catch (err) {
      console.error("Error fetching filenames:", err);
      toast.error("Could not load your profile details.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch(setProfileData({ ...profileData, [name]: value }));
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    const nextE164 = normalizeToE164(country, profileData?.phoneNumber || "");
    dispatch(setProfileData({ ...profileData, country, phoneNumber: nextE164 }));
    setErrors((prev) => ({ ...prev, phoneNumber: "" }));
  };

  const handlePhoneChange = (e) => {
    const canonical = normalizeToE164(profileData?.country, e.target.value);
    dispatch(setProfileData({ ...profileData, phoneNumber: canonical }));
    if (jobResumeUpload) {
      setErrors((prev) => ({
        ...prev,
        phoneNumber: isValidPhone(profileData?.country, canonical) ? "" : "Phone number must be 10 digits",
      }));
    }
  };

  const pickFile = (selectedFile) => {
    if (selectedFile) {
      setChosenFiles([selectedFile]);
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const updateProfile = async () => {
    if (!profileData || !profileData.first_name || !profileData.last_name || !profileData.email) {
      toast.error("Please fill out all fields.");
      throw new Error("Missing required fields");
    }
    if (fetchedProfileData && JSON.stringify(profileData) === JSON.stringify(fetchedProfileData)) return;
    const updateObj = {
      emailid: userEmail,
      modify: {
        columns: {
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          email: profileData.email,
          mobile_no: profileData.phoneNumber || "",
          country: profileData.country || "",
        },
      },
    };
    const response = await fetch(FILE_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      body: JSON.stringify(updateObj),
    });
    if (!response.ok) throw new Error("Failed to update data");
  };

  const profileResumeUpload = async () => {
    dispatch(setProfileUpdateStatus(true));
    const formData = new FormData();
    formData.append("file", file);
    formData.append("email_id", userEmail);
    try {
      const response = await fetch(RESUME_BUCKET_API, { method: "POST", body: formData });
      if (!response.ok) throw new Error("File upload failed");
      await response.json();
      dispatch(setProfileUpdateStatus(false));
    } catch (err) {
      console.error("Error uploading file:", err);
      throw err;
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("resume", file);
    formData.append("email", userEmail);
    formData.append("job_id", job.job_id);
    const response = await fetch(INTERNAL_APPLY_API, { method: "POST", body: formData });
    const data = await response.json();
    setApplicationStatus(data.status);
    setMessage(data.message);
    setFile(null);
    setChosenFiles([]);
    await fetchFilenames();
  };

  const applyNow = async () => {
    try {
      const queryObj = { email: userEmail, job_id: job.job_id, resume: fileName };
      const response = await fetch(INTERNAL_APPLY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(queryObj),
      });
      const data = await response.json();
      setApplicationStatus(data.status);
      setMessage(data.message);
    } catch (err) {
      console.error("Error submitting application:", err);
      setApplicationStatus("error");
      setMessage("Failed to submit application. Please try again later.");
    }
  };

  const submitProfileDetails = async () => {
    if (jobResumeUpload && !isValidPhone(profileData?.country, profileData?.phoneNumber)) {
      setErrors((prev) => ({ ...prev, phoneNumber: "Phone number must be 10 digits" }));
      return;
    }
    try {
      const updateObj = {
        emailid: userEmail,
        modify: {
          columns: {
            first_name: profileData.first_name,
            last_name: profileData.last_name,
            country: profileData.country,
            mobile_no: profileData.phoneNumber,
          },
        },
      };
      const response = await fetch(FILE_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(updateObj),
      });
      if (!response.ok) throw new Error("Failed to update data");
    } catch (err) {
      console.error("Error in submitProfileDetails:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (jobResumeUpload) {
      if (!isValidPhone(profileData?.country, profileData?.phoneNumber)) {
        setErrors((prev) => ({ ...prev, phoneNumber: "Phone number must be 10 digits" }));
        return;
      }
      setErrors((prev) => ({ ...prev, phoneNumber: "" }));
    }
    setSubmitting(true);
    try {
      await updateProfile();
      if (chosenFiles.length > 0) {
        await handleUpload();
        if (jobResumeUpload) {
          profileResumeUpload();
          dispatch(setJobResumeUpload(false));
          await submitProfileDetails();
        }
      } else {
        await applyNow();
      }
    } catch (err) {
      console.error("Error in submission process:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const hasResume = chosenFiles.length > 0 || !!fileName;

  return (
    <Sheet open={open} onClose={onClose} title="Easy Apply" width="min(520px, 100vw)">
      {applicationStatus !== "" ? (
        <Note tone={applicationStatus === "success" ? "accent" : "info"}>
          <LuCircleCheck size={16} />
          <span>
            <strong>{applicationStatus === "success" ? "Successfully applied" : "Already applied"}</strong>
            <br />
            {message ||
              (applicationStatus === "success"
                ? "Your resume is uploaded and you have successfully applied for this job."
                : "You have already applied for this job. Thank you!")}
          </span>
        </Note>
      ) : (
        <form onSubmit={handleSubmit} className="stack-3">
          <div>
            <div className="t-label">You are applying to</div>
            <div className="t-h3 mt-1">{job?.job_title}</div>
            <div className="t-sm">{job?.employer_name}</div>
          </div>

          <div className="grid-2">
            <div className="field">
              <span className="field-label">First name</span>
              <input
                className="input"
                type="text"
                name="first_name"
                placeholder="First name"
                value={profileData?.first_name || ""}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="field">
              <span className="field-label">Last name</span>
              <input
                className="input"
                type="text"
                name="last_name"
                placeholder="Last name"
                value={profileData?.last_name || ""}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="field">
            <span className="field-label">Email</span>
            <input className="input" type="email" name="email" value={profileData?.email || ""} readOnly required />
          </div>

          {jobResumeUpload ? (
            <>
              <div className="field">
                <span className="field-label">Country</span>
                <select
                  className="input"
                  value={profileData?.country || ""}
                  name="country"
                  onChange={handleCountryChange}
                  required
                >
                  <option value="">Select country</option>
                  <option value="USA">USA</option>
                  <option value="India">India</option>
                </select>
              </div>
              {profileData?.country ? (
                <div className="field">
                  <span className="field-label">Phone number</span>
                  <input
                    className="input"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={profileData?.phoneNumber || ""}
                    onChange={handlePhoneChange}
                    placeholder={profileData?.country === "India" ? "+91 98765 43210" : "+1 (555) 555-1234"}
                    aria-invalid={errors.phoneNumber ? "true" : "false"}
                    required
                  />
                  {errors.phoneNumber ? <span className="field-error">{errors.phoneNumber}</span> : null}
                </div>
              ) : null}
            </>
          ) : null}

          {!hasResume ? (
            <label
              className="dropzone"
              htmlFor="csx-easyapply-resume"
              onDrop={(e) => {
                e.preventDefault();
                pickFile(e.dataTransfer.files?.[0]);
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <LuUpload size={20} />
              <div className="t-sm mt-1">Drag and drop your resume here, or choose a file</div>
              <div className="t-xs">Supported formats: PDF, DOC, DOCX (max 5MB)</div>
              <input
                type="file"
                id="csx-easyapply-resume"
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
            </label>
          ) : (
            <div className="row-between panel" style={{ padding: "12px 14px" }}>
              <div className="row grow" style={{ minWidth: 0 }}>
                <LuFileText size={17} style={{ flex: "0 0 auto" }} />
                <span className="t-sm grow" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {chosenFiles.length > 0 ? chosenFiles[0].name : fileName || "Getting details"}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => {
                  setFile(null);
                  setChosenFiles([]);
                  setFileName("");
                }}
              >
                <LuX size={14} /> Replace
              </button>
            </div>
          )}

          <Btn full type="submit" className="btn-pill btn-mint" loading={submitting} disabled={submitting || !hasResume}>
            Apply now
          </Btn>
        </form>
      )}
    </Sheet>
  );
}

/* ---------------------------------------------------------------
   Job description body used as fallback prose — same data mapping
   as the legacy beautifyJobDetails (job_highlights ->
   Qualifications / Benefits / Responsibilities), sanitized.
   --------------------------------------------------------------- */
/* last-line-of-defense decode for HTML entities that survive upstream
   cleaning (double-escaped feeds); storage-side scrubbing is primary */
const ENTITY_MAP = {
  "&nbsp;": " ", "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"',
  "&#39;": "'", "&rsquo;": "'", "&lsquo;": "'", "&ldquo;": '"', "&rdquo;": '"',
  "&ndash;": "–", "&mdash;": "—", "&bull;": "•",
};
export const decodeEntities = (s) =>
  String(s || "")
    .replace(/&(?:nbsp|amp|lt|gt|quot|#39|rsquo|lsquo|ldquo|rdquo|ndash|mdash|bull);/g, (m) => ENTITY_MAP[m] || m)
    .replace(/ /g, " ");

/* ---------- "Why you match" (Lovable job-detail match panel) ----------
   Skill detection and scoring live in ../matchScore so the cards, this
   panel and the skills breakdown can never disagree about what a posting
   asks for. No extra API calls. */

export function WhyYouMatch({ job, skills }) {
  const data = useMemo(() => {
    if (!job) return null;
    const score = scoreJob(job, skills);
    const reasons = [];
    if (score && score.matched.length) {
      reasons.push(
        `You already have ${score.matched.length} of the ${score.asked} skills this role asks for`
      );
    }
    if (job.job_is_remote) reasons.push("Remote-friendly — no relocation needed");
    if (isEarlyApplicant(job.job_posted_at_datetime_utc))
      reasons.push("Posted recently — you're in the early-applicant window");
    if ((job.source || "").startsWith("ats:") || /Careers$/.test(job.job_publisher || ""))
      reasons.push(`Direct employer posting — your application goes straight to ${job.employer_name}`);
    if (!score && !reasons.length) return null;
    return { score, reasons };
  }, [job, skills]);
  if (!data) return null;
  const { score, reasons } = data;
  // analyzed jobs show their real AI match % on the detail ring too (Pavithra Jobs #3)
  const displayPct = analyzedScores.has(job.job_id) ? analyzedScores.get(job.job_id) : (score?.pct || 0);
  return (
    <div className="bene-panel match-panel">
      <div className="match-head">
        <h2 className="t-h3 row" style={{ gap: 8 }}>
          <LuSparkles size={16} className="t-accent" /> Why you match
        </h2>
        {displayPct > 0 ? <MatchRing score={displayPct} size={56} label="match" /> : null}
      </div>
      {reasons.length ? (
        <ul className="dot-list mt-2">
          {reasons.map((r) => (
            <li key={r}>
              <LuCheck size={14} className="t-accent" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {score ? (
        <div className="mt-3">
          <div className="match-label">
            Skills this role asks for
            <span className="match-count">
              {score.matched.length}/{score.asked} on your resume
            </span>
          </div>
          {/* one row, matches first — the eye lands on what you already have */}
          <div className="chip-wrap mt-2">
            {score.matched.map((s) => (
              <span key={s} className="skill-tag on">
                <LuCheck size={12} /> {s}
              </span>
            ))}
            {score.missing.map((s) => (
              <span key={s} className="skill-tag">
                {s}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* Structured renderer for plain-text job descriptions: detects section
   headings and bullet lines so ATS feeds read like a formatted posting
   instead of a wall of text. */
const DESC_HEADING_RE =
  /^(about (the )?(role|job|company|team|us|position)|responsibilities|key responsibilities|duties|what you('|’)ll (do|own|work on|bring)|what (we|they)('|’)re looking for|requirements|(preferred|minimum|basic) qualifications|qualifications|nice to have(s)?|bonus( points)?|benefits|perks|compensation( and benefits)?|salary( range)?|the role|your (role|mission|impact)|who (you are|we are)|skills( (&|and) experience)?|education|experience|why (join|us)|our (stack|tech stack|values))\b.{0,40}$/i;

/* Sections every posting carries and nobody reads while deciding whether to
   apply: scam warnings, EEO statements, privacy notices, pay-transparency
   legalese. They're demoted behind a disclosure, never dropped — the text is
   still the employer's and some of it is legally meaningful. */
const BOILERPLATE_RE =
  /^(protecting yourself|recruitment scam|please always verify|data privacy|privacy (notice|policy)|equal (employment )?opportunity|eeo|e-verify|affirmative action|accommodation|reasonable accommodation|disclaimer|legal|notice to|pay (transparency|range disclosure)|compensation disclosure|background check|drug (screen|test)|export control|itar|applicant privacy|fraud(ulent)? (job|recruit)|how we use your|third[- ]party)/i;

/* ALL-CAPS source headings ("WHAT YOU'LL DO") flip to the app's sentence-case
   heading style so the page reads as ONE hierarchy */
const normalizeHeading = (t) => {
  const s = String(t).trim();
  const letters = s.replace(/[^A-Za-z]/g, "");
  const uppers = letters.replace(/[^A-Z]/g, "");
  if (letters.length >= 4 && uppers.length / letters.length > 0.7) {
    const low = s.toLowerCase();
    return low.charAt(0).toUpperCase() + low.slice(1);
  }
  return s;
};

export function FormattedDescription({ text, leadHeading }) {
  const [noticesOpen, setNoticesOpen] = useState(false);
  const blocks = useMemo(() => {
    const out = [];
    let list = null;
    const pushList = () => {
      if (list && list.length) out.push({ t: "ul", items: list });
      list = null;
    };
    String(text || "")
      // collapse duplicated "reputed company" placeholders from ingestion (Pavithra Jobs #5)
      .replace(/(reputed company)(\s+reputed company)+/gi, "$1")
      .split(/\r?\n/)
      .forEach((raw) => {
        const line = decodeEntities(raw).trim();
        if (!line) {
          pushList();
          return;
        }
        const bullet = line.match(/^([•·▪◦*-]|\d+[.)])\s+(.*)$/);
        if (bullet) {
          (list = list || []).push(bullet[2]);
          return;
        }
        pushList();
        const bare = line.replace(/:$/, "");
        const shortLabel =
          line.length <= 24 && bare.split(/\s+/).length <= 3 && !/[.!?,;]$/.test(line);
        /* Notice headings run long ("Protecting Yourself from Recruitment
           Scams") and carry no colon, so the generic rules read them as
           body copy — match them explicitly or the whole run stays inline. */
        const isHeading =
          (line.length <= 70 &&
            (DESC_HEADING_RE.test(bare) || (/:$/.test(line) && line.length <= 48) || shortLabel)) ||
          (line.length <= 90 && BOILERPLATE_RE.test(bare));
        out.push(isHeading ? { t: "h", text: normalizeHeading(bare) } : { t: "p", text: line });
      });
    pushList();
    // one hierarchy: if the description doesn't open with its own heading,
    // the lead heading becomes the first block (no separate wrapper title)
    if (leadHeading && (!out.length || out[0].t !== "h")) {
      out.unshift({ t: "h", text: leadHeading });
    }
    return out;
  }, [text, leadHeading]);

  /* group into sections; consecutive list-only sections pair side-by-side
     (Lovable's "What you'll do" / "What they're looking for" 2-col grid) */
  const sections = useMemo(() => {
    const secs = [];
    let cur = null;
    blocks.forEach((b) => {
      if (b.t === "h") {
        cur = { h: b.text, body: [] };
        secs.push(cur);
      } else {
        if (!cur) {
          cur = { h: null, body: [] };
          secs.push(cur);
        }
        cur.body.push(b);
      }
    });
    return secs;
  }, [blocks]);

  const isPairable = (s) => s && s.h && s.body.length > 0 && s.body.every((b) => b.t === "ul");

  const renderSection = (s, key) => (
    <section key={key} className="jdx-sec">
      {s.h ? <h2 className="t-h3 jdx-h">{s.h}</h2> : null}
      {s.body.map((b, k) =>
        b.t === "ul" ? (
          <SugList key={k} items={b.items} />
        ) : (
          <p key={k} className="t-sm jdx-p">
            {b.text}
          </p>
        )
      )}
    </section>
  );

  /* Once a boilerplate heading appears, everything after it is boilerplate
     too — these notices always sit in a run at the foot of a posting, and
     their sub-headings ("Data Privacy") don't all match on their own. */
  const firstNotice = sections.findIndex((s) => s.h && BOILERPLATE_RE.test(s.h));
  const main = firstNotice === -1 ? sections : sections.slice(0, firstNotice);
  const notices = firstNotice === -1 ? [] : sections.slice(firstNotice);

  const rendered = [];
  for (let i = 0; i < main.length; i++) {
    if (isPairable(main[i]) && isPairable(main[i + 1])) {
      rendered.push(
        <div key={`pair-${i}`} className="jdx-cols">
          {renderSection(main[i], `a-${i}`)}
          {renderSection(main[i + 1], `b-${i}`)}
        </div>
      );
      i += 1;
    } else {
      rendered.push(renderSection(main[i], i));
    }
  }

  /* ported Lovable conventions: display-font section headings (t-h3), muted
     relaxed body, the app's dot-list bullets — no bespoke type styles */
  return (
    <div className="jdx">
      {rendered}
      {notices.length ? (
        <div className="jdx-notices">
          <button
            type="button"
            className="jdx-notices-toggle"
            onClick={() => setNoticesOpen((v) => !v)}
            aria-expanded={noticesOpen}
          >
            <LuChevronDown size={14} className={noticesOpen ? "rot" : ""} />
            {noticesOpen ? "Hide" : "Show"} legal &amp; company notices
            <span className="jdx-notices-n">{notices.length}</span>
          </button>
          {noticesOpen ? <div className="mt-2">{notices.map(renderSection)}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export function JobHighlightsProse({ job }) {
  const html = useMemo(() => {
    const formatList = (list) => list.map((item) => `<li>${item}</li>`).join("");
    const jobHighlights = job?.job_highlights || {};
    const qualifications = jobHighlights.Qualifications
      ? `<ul>${formatList(jobHighlights.Qualifications)}</ul>`
      : "<p>No qualifications provided.</p>";
    const benefits = jobHighlights.Benefits
      ? `<ul>${formatList(jobHighlights.Benefits)}</ul>`
      : "<p>No benefits provided.</p>";
    const responsibilities = jobHighlights.Responsibilities
      ? `<ul>${formatList(jobHighlights.Responsibilities)}</ul>`
      : "<p>No responsibilities provided.</p>";
    return DOMPurify.sanitize(
      `<h3>Qualifications</h3>${qualifications}<h3>Benefits</h3>${benefits}<h3>Responsibilities</h3>${responsibilities}`
    );
  }, [job]);
  // eslint-disable-next-line react/no-danger
  return <div className="prose" dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ===============================================================
   Job search page — Lovable _app.jobs.index.tsx layout:
   glass filter bar on top, list on the left, detail on the right.
   =============================================================== */
export default function JobSearch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userEmail = useSelector((s) => s.auth.auth.email);
  const { apiToken, apiTokenReady, fileResume, featuresToBlock } = useSelector((s) => s.profile);
  const storedJobs = useSelector((s) => s.profile.jobsList);
  const jobsList = useMemo(() => (Array.isArray(storedJobs) ? storedJobs : []), [storedJobs]);

  const isInternBlocked = featuresToBlock.includes("block_intern");
  const isFulltimeBlocked = featuresToBlock.includes("block_intern"); // legacy quirk: same flag gates both

  const jobsRef = useRef(Array.isArray(storedJobs) ? [...storedJobs] : []);
  const fetchStartedRef = useRef(false);

  /* start in the loading state when there is nothing cached yet, so the
     first paint shows skeletons (legacy covered entry with a loader)
     instead of flashing the empty state before the stream begins */
  const [isLoading, setIsLoading] = useState(() => !(Array.isArray(storedJobs) && storedJobs.length > 0));
  const [streaming, setStreaming] = useState(false);
  const [jobsReady, setJobsReady] = useState(false);
  /* Filtering is synchronous, so the list snapped to its new contents with no
     sign anything had happened — on a big change that reads as a glitch.
     A brief pulse acknowledges the input without delaying the result. */
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState(null);

  const [activeJobId, setActiveJobId] = useState(null);
  /* On a phone the detail panel renders BELOW the whole list, so tapping a
     card looked like nothing happened. Mobile gets a full-screen detail view
     with its own header instead; desktop keeps the side-by-side split. */
  const [detailOpen, setDetailOpen] = useState(false);
  const [buttonText, setButtonText] = useState("Apply");

  const { savedIds, toggleSaved } = useSavedJobs();

  /* filters */
  const [searchTerm, setSearchTerm] = useState("");
  const [whereFilter, setWhereFilter] = useState(""); // client-side, over fetched city/state/country
  const [internshipFilter, setInternshipFilter] = useState(false);
  const [fulltimeFilter, setFulltimeFilter] = useState(false);
  const [contractorFilter, setContractorFilter] = useState(false);
  const [remoteJobsFilter, setRemoteJobsFilter] = useState(false);
  /* newest first by default — a stale posting is usually already closed, so
     recency beats a marginally better skills overlap on first load */
  const [recentJobsFilter, setRecentJobsFilter] = useState(true);
  const [moreFilters, setMoreFilters] = useState(false);
  const [daysFilter, setDaysFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [publisherFilter, setPublisherFilter] = useState("");
  const [employerFilter, setEmployerFilter] = useState("");

  /* sheets */
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [autoStudio, setAutoStudio] = useState(false);
  const [autoApplyOpen, setAutoApplyOpen] = useState(false);
  const [easyApplyOpen, setEasyApplyOpen] = useState(false);
  const [upgradeFilters, setUpgradeFilters] = useState(false);

  /* ------- default job-function scope: the page loads ONLY the user's
     selected technology / job function; the selector refetches. "__default__"
     resolves server-side to the user's top technology. ------- */
  const fnStorageKey = `cs_job_fn:${userEmail || "anon"}`;
  const [jobFunction, setJobFunction] = useState(() => {
    try {
      return localStorage.getItem(`cs_job_fn:${JSON.parse(localStorage.getItem("userDetails") || "{}").email || "anon"}`) || "__default__";
    } catch {
      return "__default__";
    }
  });
  const [functionOptions, setFunctionOptions] = useState([]);
  const [activeFunction, setActiveFunction] = useState(null); // resolved label from the stream
  const [matchMode, setMatchMode] = useState(null); // 'title' | 'description' | 'profile'
  const [userSkills, setUserSkills] = useState(() => {
    try {
      const arr = JSON.parse(sessionStorage.getItem("cs_user_skills") || "[]");
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });

  /* ------- streaming jobs fetch (legacy loop preserved) ------- */
  const fetchJobsList = async (fnOverride) => {
    const fn = fnOverride || jobFunction || "__default__";
    let loadedFn = null;
    try {
      loadedFn = sessionStorage.getItem("cs_jobs_fn_loaded");
    } catch {}
    if (jobsRef.current.length > 0 && loadedFn === fn) {
      setIsLoading(false);
      return;
    }
    jobsRef.current = [];
    dispatch(setJobsList([]));
    setActiveJobId(null);
    setJobsReady(false);
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(JOBS_STREAM_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({ email_id: userEmail, job_function: fn }),
      });
      /* 404 is how the backend says "nothing to show" — no jobs for this filter,
         no resume on file yet, no user record. That is an empty state, not a
         failure, so render it as one instead of an error banner. */
      if (response.status === 404) {
        jobsRef.current = [];
        dispatch(setJobsList([]));
        setStreaming(false);
        setJobsReady(true);
        setIsLoading(false);
        try {
          sessionStorage.setItem("cs_jobs_fn_loaded", fn);
        } catch {}
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body?.getReader();
      if (!reader) throw new Error("ReadableStream not supported");

      const decoder = new TextDecoder();
      let buffer = "";
      setStreaming(true);

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // keep incomplete data in the buffer

        const newJobs = [];
        for (const line of lines) {
          if (line.trim()) {
            try {
              const jsonStr = line.startsWith("data: ") ? line.slice(6) : line;
              const parsedData = JSON.parse(jsonStr);
              if (parsedData.status === "complete") {
                setJobsReady(true);
              } else if (parsedData.profile) {
                setFunctionOptions(parsedData.profile.options || []);
                setActiveFunction(
                  parsedData.profile.selected_function || parsedData.profile.default_function || null
                );
                setMatchMode(parsedData.profile.match_mode || null);
                if (Array.isArray(parsedData.profile.skills) && parsedData.profile.skills.length) {
                  setUserSkills(parsedData.profile.skills);
                  try {
                    sessionStorage.setItem("cs_user_skills", JSON.stringify(parsedData.profile.skills));
                  } catch {}
                }
              } else if (parsedData.matches) {
                newJobs.push(...parsedData.matches.map(normalizeJobRecord));
              }
            } catch (err) {
              console.warn("Error parsing line:", err, line);
            }
          }
        }

        if (newJobs.length > 0) {
          jobsRef.current = [...jobsRef.current, ...newJobs];
          dispatch(setJobsList(jobsRef.current));
          setIsLoading(false); // reveal cards incrementally as chunks arrive
        }
      }

      if (buffer.trim()) {
        try {
          const jsonStr = buffer.startsWith("data: ") ? buffer.slice(6) : buffer;
          const parsedData = JSON.parse(jsonStr);
          if (parsedData.matches) {
            jobsRef.current = [...jobsRef.current, ...parsedData.matches.map(normalizeJobRecord)];
            dispatch(setJobsList(jobsRef.current));
          }
        } catch (err) {
          console.warn("Error parsing final buffer:", err);
        }
      }

      setStreaming(false);
      setJobsReady(true);
      setIsLoading(false);
      try {
        sessionStorage.setItem("cs_jobs_fn_loaded", fn);
      } catch {}
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError(err.message);
      setIsLoading(false);
      setStreaming(false);
    }
  };

  const savedFnKey = `cs_job_fn_saved:${userEmail || "anon"}`;
  const [savedFunctions, setSavedFunctions] = useState(() => {
    try {
      const arr = JSON.parse(localStorage.getItem(`cs_job_fn_saved:${JSON.parse(localStorage.getItem("userDetails") || "{}").email || "anon"}`) || "[]");
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });

  const changeJobFunction = (fn) => {
    if (!fn || fn === jobFunction) return;
    setJobFunction(fn);
    try {
      localStorage.setItem(fnStorageKey, fn);
    } catch {}
    // a custom term (typed search saved as a focus) joins the saved list so it
    // stays available in the selector next time
    if (
      fn !== "__default__" &&
      fn !== "__all__" &&
      !functionOptions.some((o) => o.toLowerCase() === fn.toLowerCase()) &&
      !savedFunctions.some((o) => o.toLowerCase() === fn.toLowerCase())
    ) {
      const next = [...savedFunctions, fn].slice(-8);
      setSavedFunctions(next);
      try {
        localStorage.setItem(savedFnKey, JSON.stringify(next));
      } catch {}
    }
    setSearchTerm("");
    fetchJobsList(fn);
  };

  useEffect(() => {
    if (userEmail !== "" && apiToken !== "" && !fetchStartedRef.current) {
      fetchStartedRef.current = true;
      fetchJobsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady]);

  /* ------- resume filename (legacy fetchFilenames) ------- */
  const fetchFilenames = async () => {
    if (fileResume) return;
    try {
      const queryObj = { emailid: userEmail };
      const response = await fetch(FILE_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify(queryObj),
      });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      const filenames = data.file_details.map((f) => f.file_name);
      dispatch(setFileResume(filenames[0]));
    } catch (err) {
      console.error("Error fetching resume filename:", err);
    }
  };

  useEffect(() => {
    if (userEmail !== "" && apiToken !== "") fetchFilenames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady]);

  /* ------- selection ------- */
  useEffect(() => {
    if (!activeJobId && jobsList.length > 0 && jobsList[0]?.job_id) {
      setActiveJobId(jobsList[0].job_id);
    }
  }, [jobsList, activeJobId]);

  const selectedJob = useMemo(
    () => jobsList.find((j) => j.job_id === activeJobId) || null,
    [jobsList, activeJobId]
  );

  /* ------- apply / reapply state (legacy payloads) ------- */
  const checkJobApplied = async () => {
    try {
      if (!userEmail) {
        console.error("User email is not available.");
        return;
      }
      const appliedResponse = await fetch(APPLY_CHECK_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          user_email: userEmail,
          job_id: activeJobId,
        }),
      });
      if (!appliedResponse.ok) throw new Error("Failed to check job application status");
      const data = await appliedResponse.json();
      setButtonText(data.result === "Reapply Button" ? "Applied" : "Apply");
    } catch (err) {
      console.error("Error checking apply status:", err);
    }
  };

  useEffect(() => {
    if (activeJobId && userEmail !== "" && apiToken !== "") {
      setButtonText("Apply");
      checkJobApplied();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobId, userEmail, insightsOpen, apiTokenReady]);

  const applyButton = async () => {
    try {
      if (!userEmail) {
        console.error("User email is not available.");
        return;
      }
      const response = await fetch(APPLY_CHECK_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          user_email: userEmail,
          payload: {
            email: userEmail,
            job_id: activeJobId,
          },
          function_name: "clicked on Apply now",
          insert_log: true,
        }),
      });
      if (!response.ok) throw new Error("Failed to send data to the server");
      await response.json();
      checkJobApplied();
    } catch (err) {
      console.error("Error:", err.message);
    }
  };

  /* ------- client-side filtering (legacy ListTab logic) ------- */
  const filteredJobs = useMemo(() => {
    let filtered = jobsList || [];

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.job_title?.toLowerCase().includes(lowerCaseSearchTerm) ||
          job.employer_name?.toLowerCase().includes(lowerCaseSearchTerm) ||
          job.job_city?.toLowerCase().includes(lowerCaseSearchTerm) ||
          job.job_state?.toLowerCase().includes(lowerCaseSearchTerm) ||
          job.job_country?.toLowerCase().includes(lowerCaseSearchTerm) ||
          job.job_employment_type?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    if (whereFilter.trim()) {
      const w = whereFilter.trim().toLowerCase();
      filtered = filtered.filter((job) =>
        `${job.job_city || ""} ${job.job_state || ""} ${job.job_country || ""}`.toLowerCase().includes(w)
      );
    }
    if (daysFilter) {
      const currentDate = new Date();
      filtered = filtered.filter((job) => {
        const jobPostingDate = new Date(job.job_posted_at_datetime_utc);
        const timeDiff = (currentDate - jobPostingDate) / (1000 * 60 * 60 * 24);
        return (
          (daysFilter === "24 hours" && timeDiff <= 1) ||
          (daysFilter === "last 3 days" && timeDiff <= 3) ||
          (daysFilter === "last 7 days" && timeDiff <= 7)
        );
      });
    }
    if (stateFilter) filtered = filtered.filter((job) => job.job_state === stateFilter);
    if (cityFilter) filtered = filtered.filter((job) => job.job_city === cityFilter);
    if (remoteJobsFilter) filtered = filtered.filter((job) => job.job_is_remote === true);
    if (internshipFilter || fulltimeFilter || contractorFilter) {
      /* Feeds use varied forms ("Full-time", "FULLTIME", "full_time", "Contract",
         "Contractor", "Temporary", "Internship") — normalise before matching so
         the chips actually surface matching jobs (Pavithra Jobs #6). */
      const norm = (job) => (job.job_employment_type || "").toUpperCase().replace(/[^A-Z]/g, "");
      filtered = filtered.filter((job) => {
        const et = norm(job);
        return (
          (internshipFilter && et.includes("INTERN")) ||
          (fulltimeFilter && (et.includes("FULLTIME") || et.includes("FULL"))) ||
          (contractorFilter && (et.includes("CONTRACT") || et.includes("TEMP")))
        );
      });
    }
    if (jobTitleFilter) filtered = filtered.filter((job) => job.job_title === jobTitleFilter);
    if (publisherFilter) filtered = filtered.filter((job) => job.job_publisher === publisherFilter);
    if (employerFilter) filtered = filtered.filter((job) => job.employer_name === employerFilter);
    if (recentJobsFilter) {
      filtered = [...filtered].sort(
        (a, b) => new Date(b.job_posted_at_datetime_utc) - new Date(a.job_posted_at_datetime_utc)
      );
    } else {
      /* "Best match" must put the HIGHEST match % first (Pavithra Jobs #7).
         Score each job once, then sort descending. */
      const sc = new Map(filtered.map((j) => [j.job_id, scoreJob(j, userSkills)?.pct || 0]));
      filtered = [...filtered].sort((a, b) => (sc.get(b.job_id) || 0) - (sc.get(a.job_id) || 0));
    }
    return filtered;
  }, [
    jobsList,
    searchTerm,
    whereFilter,
    daysFilter,
    stateFilter,
    cityFilter,
    remoteJobsFilter,
    internshipFilter,
    fulltimeFilter,
    contractorFilter,
    jobTitleFilter,
    publisherFilter,
    employerFilter,
    recentJobsFilter,
    userSkills,
  ]);

  /* Keep the detail pane in sync with the filtered list — never show a job the
     current filters have hidden (Pavithra Jobs #2). */
  useEffect(() => {
    if (filteredJobs.length && !filteredJobs.some((j) => j.job_id === activeJobId)) {
      setActiveJobId(filteredJobs[0].job_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredJobs]);

  const uniqueOf = (key) => [...new Set(jobsList.map((item) => item[key]))].filter(Boolean);

  const clearFilters = () => {
    setSearchTerm("");
    setWhereFilter("");
    setInternshipFilter(false);
    setFulltimeFilter(false);
    setContractorFilter(false);
    setRemoteJobsFilter(false);
    setRecentJobsFilter(false);
    setDaysFilter("");
    setStateFilter("");
    setCityFilter("");
    setJobTitleFilter("");
    setPublisherFilter("");
    setEmployerFilter("");
  };

  const chip = (label, on, toggle, locked) => (
    <button
      type="button"
      key={label}
      className={`chip${on ? " on" : ""}${locked ? " chip-lock" : ""}`}
      onClick={() => (locked ? setUpgradeFilters(true) : toggle())}
    >
      {locked ? <LuLock size={13} /> : null}
      {label}
    </button>
  );

  const showSkeletons = isLoading && jobsList.length === 0 && !error;
  useEffect(() => {
    if (streaming) return undefined;
    setFiltering(true);
    const t = setTimeout(() => setFiltering(false), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    whereFilter,
    stateFilter,
    cityFilter,
    daysFilter,
    jobTitleFilter,
    recentJobsFilter,
    internshipFilter,
    fulltimeFilter,
    contractorFilter,
    remoteJobsFilter,
  ]);

  const showEmpty = !isLoading && !streaming && filteredJobs.length === 0 && !error;

  /* selected-job derived presentation data (real fetched fields only) */
  const selHighlights = selectedJob?.job_highlights || {};
  const selResponsibilities =
    Array.isArray(selHighlights.Responsibilities) && selHighlights.Responsibilities.length > 0
      ? selHighlights.Responsibilities
      : null;
  const selQualifications = (() => {
    const q = Array.isArray(selHighlights.Qualifications)
      ? selHighlights.Qualifications.filter((x) => String(x).trim())
      : [];
    if (!q.length) return null;
    // the backend falls back to stuffing the whole description into
    // Qualifications — don't render the same wall of text twice
    if (q.length === 1 && String(q[0]).trim() === String(selectedJob?.job_description || "").trim())
      return null;
    return q;
  })();
  const selBenefits =
    Array.isArray(selHighlights.Benefits) && selHighlights.Benefits.filter((b) => String(b).trim()).length > 0
      ? selHighlights.Benefits.filter((b) => String(b).trim())
      : null;
  /* The human-readable function currently in play, or null when no single
     function applies. "__default__" resolves to whatever the stream picked;
     "__all__" means no filter at all, so there is no label to show. */
  const functionLabel =
    jobFunction === "__all__"
      ? null
      : jobFunction === "__default__"
      ? activeFunction || null
      : jobFunction || null;

  /* option list for the focus selector: profile suggestions, then saved
     customs, then whatever is currently applied if it is neither */
  const fnOptions = useMemo(() => {
    const out = [];
    if (jobFunction === "__default__" && !activeFunction) {
      out.push({ value: "__default__", label: "My top match…" });
    }
    functionOptions.forEach((o) => out.push({ value: o, label: o }));
    savedFunctions
      .filter((o) => !functionOptions.some((p) => p.toLowerCase() === o.toLowerCase()))
      .forEach((o) => out.push({ value: o, label: o }));
    if (
      jobFunction !== "__default__" &&
      jobFunction !== "__all__" &&
      !functionOptions.includes(jobFunction) &&
      !savedFunctions.includes(jobFunction)
    ) {
      out.push({ value: jobFunction, label: jobFunction });
    }
    out.push({ value: "__all__", label: "All my matches" });
    return out;
  }, [functionOptions, savedFunctions, jobFunction, activeFunction]);

  /* scoreJob runs ~100 alias regexes over a full job description. Called
     inline in the card map it re-ran for EVERY job on EVERY render, so just
     selecting a different job re-scored the whole list and the page crawled.
     Compute once per job and look the result up by id. */
  const jobMeta = useMemo(() => {
    const map = new Map();
    filteredJobs.forEach((job) => {
      map.set(job.job_id, { score: scoreJob(job, userSkills), facts: jobFacts(job) });
    });
    return map;
  }, [filteredJobs, userSkills]);

  const selFacts = jobFacts(selectedJob);
  const selSaved = selectedJob ? savedIds.includes(selectedJob.job_id) : false;

  return (
    <>
      <div className="stack-3">
        {/* ------------ compact filter rail (Jobright-style single row) ------------ */}
        <div className="ld-glass jobs-filterbar compact">
          <div className="filter-row">
            <Select
              className={`csel-strong${streaming ? " is-busy" : ""}`}
              value={jobFunction === "__default__" ? activeFunction || "__default__" : jobFunction}
              onChange={(e) => changeJobFunction(e.target.value)}
              ariaLabel="Job function focus"
              options={fnOptions}
            />
            <div className="input-wrap has-icon filter-search">
              <span className="input-icon">
                <LuSearch size={15} />
              </span>
              <input
                className="input input-pill"
                type="text"
                placeholder="Role, company, or keyword"
                title="Enter loads the term as a focus"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchTerm.trim().length >= 2 && !streaming) {
                    changeJobFunction(searchTerm.trim());
                  }
                }}
              />
            </div>
            <div className="input-wrap has-icon filter-city">
              <span className="input-icon">
                <LuMapPin size={15} />
              </span>
              <input
                className="input input-pill"
                type="text"
                placeholder="City or state"
                value={whereFilter}
                onChange={(e) => setWhereFilter(e.target.value)}
              />
            </div>
            {chip("Intern", internshipFilter, () => setInternshipFilter((v) => !v), isInternBlocked)}
            {chip("Full time", fulltimeFilter, () => setFulltimeFilter((v) => !v), isFulltimeBlocked)}
            {chip("Contract", contractorFilter, () => setContractorFilter((v) => !v), false)}
            {chip("Remote", remoteJobsFilter, () => setRemoteJobsFilter((v) => !v), false)}
            <button
              type="button"
              className={`chip${moreFilters ? " on" : ""}`}
              onClick={() => setMoreFilters((v) => !v)}
            >
              <LuSlidersHorizontal size={13} /> More filters
            </button>
            <Select
              className={`csel-auto${daysFilter ? " is-set" : ""}`}
              value={daysFilter}
              onChange={(e) => setDaysFilter(e.target.value)}
              ariaLabel="Posted time"
              options={[
                { value: "", label: "Posted any time" },
                { value: "24 hours", label: "24 hours" },
                { value: "last 3 days", label: "Last 3 days" },
                { value: "last 7 days", label: "Last 7 days" },
              ]}
            />
            <Select
              className={recentJobsFilter ? "is-set" : ""}
              value={recentJobsFilter ? "recent" : "match"}
              onChange={(e) => setRecentJobsFilter(e.target.value === "recent")}
              ariaLabel="Sort"
              options={[
                { value: "match", label: "Best match" },
                { value: "recent", label: "Most recent" },
              ]}
            />
            <Btn
              variant="ghost"
              size="sm"
              className="btn-pill"
              onClick={() => navigate("/job-match")}
              title="Paste a job description and see how your resume matches"
            >
              <LuSparkles size={14} /> Match a job
            </Btn>
            <span className="filterbar-count">
              <LuSlidersHorizontal size={14} />
              {filteredJobs.length}
            </span>
          </div>

          {/* Only meaningful when ONE function is in play. "__all__" is an internal
              sentinel meaning "no filter" — it was being printed to users verbatim,
              and there is no single title to report a miss against anyway. */}
          {matchMode === "description" && !streaming && functionLabel ? (
            <span className="fn-mode-note">
              <LuInfo size={13} />
              No titles match “{functionLabel}” yet —
              showing roles that mention it, most-mentioned first
            </span>
          ) : null}

          {moreFilters ? (
            <div className="jobs-selects">
              <select
                className="input select-pill"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              >
                <option value="">All states</option>
                {uniqueOf("job_state").map((v, i) => (
                  <option key={`st-${i}`} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select className="input select-pill" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
                <option value="">All cities</option>
                {uniqueOf("job_city").map((v, i) => (
                  <option key={`ct-${i}`} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select
                className="input select-pill"
                value={jobTitleFilter}
                onChange={(e) => setJobTitleFilter(e.target.value)}
              >
                <option value="">All roles</option>
                {uniqueOf("job_title").map((v, i) => (
                  <option key={`ro-${i}`} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select
                className="input select-pill"
                value={publisherFilter}
                onChange={(e) => setPublisherFilter(e.target.value)}
              >
                <option value="">All publishers</option>
                {uniqueOf("job_publisher").map((v, i) => (
                  <option key={`pu-${i}`} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select
                className="input select-pill"
                value={employerFilter}
                onChange={(e) => setEmployerFilter(e.target.value)}
              >
                <option value="">All employers</option>
                {uniqueOf("employer_name").map((v, i) => (
                  <option key={`em-${i}`} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>

        {error ? (
          <Note tone="danger">
            <LuInfo size={16} />
            <span className="grow">Could not load your job matches: {error}</span>
            <Btn
              size="sm"
              variant="ghost"
              onClick={() => {
                setError(null);
                fetchJobsList();
              }}
            >
              <LuRefreshCw size={14} /> Retry
            </Btn>
          </Note>
        ) : null}

        {/* ------------ empty state (Lovable dashed tile) ------------ */}
        {showEmpty ? (
          <div className="ld-empty">
            <span className="ld-empty-ic">
              <LuBriefcase size={20} />
            </span>
            <div className="ld-empty-title">No jobs match those filters</div>
            <div className="ld-empty-sub">
              {searchTerm.trim().length >= 2
                ? `Nothing loaded matches "${searchTerm.trim()}" — search all jobs for it instead.`
                : "Try a broader search term or clear the location filter."}
            </div>
            <div className="ld-empty-actions">
              {searchTerm.trim().length >= 2 ? (
                <button
                  type="button"
                  className="btn btn-pill btn-mint"
                  disabled={streaming}
                  onClick={() => changeJobFunction(searchTerm.trim())}
                >
                  <LuSearch size={15} /> Load all “{searchTerm.trim()}” jobs & save as focus
                </button>
              ) : null}
              <Btn variant="ghost" className="btn-pill" onClick={clearFilters}>
                Clear filters
              </Btn>
              {!fileResume ? (
                <Btn to="/resume-upload" variant="soft" className="btn-pill">
                  <LuUpload size={15} /> Upload your resume
                </Btn>
              ) : null}
            </div>
          </div>
        ) : (
          /* ------------ split view: list + detail ------------ */
          <div className="jobs-layout">
            <div className="job-list job-list-scroll">
              {showSkeletons
                ? [...Array(5)].map((_, i) => (
                    <Card className="job-card" key={`sk-${i}`} style={{ borderRadius: 18 }}>
                      <div className="job-top">
                        <Skel w={42} h={42} r={12} />
                        <div className="grow stack-2">
                          <Skel w="70%" h={16} />
                          <Skel w="50%" h={12} />
                        </div>
                        <Skel w={44} h={44} r={22} />
                      </div>
                      <div className="stack-2 mt-2">
                        <Skel w="90%" h={12} />
                        <Skel w="60%" h={12} />
                      </div>
                    </Card>
                  ))
                : null}

              {filtering ? (
                <div className="stream-row">
                  <Spinner /> Updating results…
                </div>
              ) : null}

              {filteredJobs.map((job) => {
                const on = job.job_id === activeJobId;
                const meta = jobMeta.get(job.job_id) || {};
                const score = meta.score;
                const facts = meta.facts || {};
                // analyzed jobs show their real AI match % on the list ring too (Pavithra Jobs #3)
                const cardPct = analyzedScores.has(job.job_id) ? analyzedScores.get(job.job_id) : (score?.pct || 0);
                return (
                  <Card
                    hover
                    key={job.job_id}
                    className={`job-card${on ? " on" : ""}`}
                    style={{ borderRadius: 18 }}
                    onClick={() => {
                      setActiveJobId(job.job_id);
                      setDetailOpen(true);
                    }}
                  >
                    <div className="job-top">
                      <span className="job-logo">{companyInitials(job.employer_name)}</span>
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="job-card-title trunc">{job.job_title}</div>
                        <div className="t-xs trunc">
                          {job.employer_name}
                          {job.job_city ? ` · ${job.job_city}, ${job.job_state}` : ""}
                          {` · ${job.job_is_remote ? "Remote" : "Onsite"}`}
                        </div>
                      </div>
                      {cardPct > 0 ? <MatchRing score={cardPct} size={44} label="match" /> : null}
                    </div>
                    <p className="t-sm clamp-2 mt-2">{job.job_description}</p>
                    {/* stated facts first, then status — scanning a list is
                        about comparing roles, not re-reading your own state */}
                    <div className="job-chips">
                      {job.salary_range ? (
                        <span className="fact sal">
                          <LuBanknote size={12} /> {job.salary_range}
                        </span>
                      ) : null}
                      <span className="fact">
                        {job.job_is_remote ? <LuHouse size={12} /> : <LuBuilding2 size={12} />}{" "}
                        {job.job_is_remote ? "Remote" : "Onsite"}
                      </span>
                      {facts.seniority ? (
                        <span className="fact">
                          <LuTrendingUp size={12} /> {facts.seniority}
                        </span>
                      ) : null}
                      {facts.yearsRequired ? (
                        <span className="fact">
                          <LuBriefcase size={12} /> {facts.yearsRequired}+ yrs
                        </span>
                      ) : null}
                      {job.job_employment_type ? (
                        <span className="fact">
                          <LuFileText size={12} /> {titleCase(job.job_employment_type)}
                        </span>
                      ) : null}
                    </div>
                    <div className="job-card-meta">
                      <span className="row" style={{ gap: 4 }}>
                        <LuClock size={12} /> {timeAgo(job.job_posted_at_datetime_utc)}
                      </span>
                      {isEarlyApplicant(job.job_posted_at_datetime_utc) ? (
                        <Badge tone="accent">Early applicant</Badge>
                      ) : null}
                      {job.job_source === "Internal" ? <Badge tone="info">Easy Apply</Badge> : null}
                      {on && buttonText === "Applied" ? <Badge tone="amber">Applied before</Badge> : null}
                      {savedIds.includes(job.job_id) ? <Badge tone="plain">Saved</Badge> : null}
                    </div>
                  </Card>
                );
              })}

              {streaming && !jobsReady ? (
                <div className="stream-row">
                  <Spinner /> Finding matches…
                </div>
              ) : null}
            </div>

            {/* ------------ right: detail panel ------------ */}
            <Card
              className={`job-detail-panel${detailOpen ? " detail-open" : ""}`}
              pad
              style={{ borderRadius: 24 }}
            >
              {selectedJob ? (
                <div className="stack-3">
                  {/* mobile-only: back to the list + which job you are reading */}
                  <div className="detail-mobile-head">
                    <button
                      type="button"
                      className="detail-back"
                      onClick={() => setDetailOpen(false)}
                      aria-label="Back to job list"
                    >
                      <LuArrowLeft size={18} />
                    </button>
                    <div className="detail-mobile-title">
                      <div className="trunc">{selectedJob.job_title}</div>
                      <div className="t-xs trunc">{selectedJob.employer_name}</div>
                    </div>
                  </div>
                  <div className="detail-actions">
                    <button type="button" className="btn btn-pill btn-mint" onClick={() => setInsightsOpen(true)}>
                      <LuSparkles size={15} /> Analyze match
                    </button>
                    {/* the wizard used to be reachable only by opening the analysis
                        and waiting out the stream — this runs the same analysis but
                        goes straight to the tailoring steps when it lands */}
                    <Btn
                      variant="ghost"
                      className="btn-pill"
                      onClick={() => {
                        setAutoStudio(true);
                        setInsightsOpen(true);
                      }}
                    >
                      <LuFileText size={15} /> Tailor resume
                    </Btn>
                    {selectedJob.job_source === "Internal" ? (
                      <Btn variant="ghost" className="btn-pill" onClick={() => setEasyApplyOpen(true)}>
                        <LuZap size={15} /> Easy Apply
                      </Btn>
                    ) : (
                      <a
                        href={selectedJob.job_apply_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost btn-pill"
                        onClick={applyButton}
                      >
                        {buttonText === "Applied" ? (
                          <LuCircleCheck size={15} className="t-accent" />
                        ) : (
                          <LuExternalLink size={15} />
                        )}{" "}
                        {buttonText}
                      </a>
                    )}
                    <Btn variant="quiet" className="btn-pill" onClick={() => toggleSaved(selectedJob.job_id)}>
                      {selSaved ? <LuBookmarkCheck size={15} className="t-accent" /> : <LuBookmark size={15} />}
                      {selSaved ? "Saved" : "Save"}
                    </Btn>
                    <Btn variant="quiet" className="btn-pill" onClick={() => setAutoApplyOpen(true)}>
                      <LuZap size={15} /> Auto Apply
                    </Btn>
                    <Link
                      to={`/job/detailed?id=${selectedJob.job_id}`}
                      className="btn btn-quiet btn-pill"
                      style={{ marginLeft: "auto" }}
                      title="Open full page"
                    >
                      Open full page
                    </Link>
                  </div>

                  <div className="job-top">
                    <span className="job-logo-lg">{companyInitials(selectedJob.employer_name)}</span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <h1 className="t-h2">{selectedJob.job_title}</h1>
                      <div className="meta-row t-sm mt-1">
                        <span>{selectedJob.employer_name}</span>
                        <span className="meta-dot">
                          <LuMapPin size={12} /> {selectedJob.job_city}, {selectedJob.job_state},{" "}
                          {selectedJob.job_country}
                        </span>
                        <span className="meta-dot">{selectedJob.job_is_remote ? "Remote" : "Onsite"}</span>
                        {selectedJob.job_employment_type ? (
                          <span className="meta-dot">{titleCase(selectedJob.job_employment_type)}</span>
                        ) : null}
                        {selFacts.seniority ? <span className="meta-dot">{selFacts.seniority}</span> : null}
                        {selFacts.yearsRequired ? (
                          <span className="meta-dot">{selFacts.yearsRequired}+ yrs exp</span>
                        ) : null}
                      </div>
                      <div className="meta-row t-xs mt-1">
                        {selectedJob.salary_range ? (
                          <span className="t-ink" style={{ fontWeight: 600 }}>
                            {selectedJob.salary_range}
                          </span>
                        ) : null}
                        {selectedJob.job_publisher ? (
                          <span className="row" style={{ gap: 4 }}>
                            <LuNewspaper size={12} /> {selectedJob.job_publisher}
                          </span>
                        ) : null}
                        <span className="meta-dot">
                          <LuClock size={12} /> {timeAgo(selectedJob.job_posted_at_datetime_utc)}
                        </span>
                      </div>
                    </div>
                    {/* the score lives in the Why-you-match panel just below,
                        where the skill breakdown explains it */}
                  </div>

                  <hr className="divider" />

                  <WhyYouMatch job={selectedJob} skills={userSkills} />

                  {selectedJob.job_description ? (
                    <section>
                      <FormattedDescription text={selectedJob.job_description} leadHeading="About the role" />
                    </section>
                  ) : null}

                  {selResponsibilities || selQualifications ? (
                    <div className="role-cols">
                      {selResponsibilities ? (
                        <section>
                          <h2 className="t-h3 mb-2">What you'll do</h2>
                          <SugList items={selResponsibilities} />
                        </section>
                      ) : null}
                      {selQualifications ? (
                        <section>
                          <h2 className="t-h3 mb-2">What they're looking for</h2>
                          <SugList items={selQualifications} dim />
                        </section>
                      ) : null}
                    </div>
                  ) : null}

                  {selBenefits ? (
                    <div className="bene-panel">
                      <h2 className="t-h3 row" style={{ gap: 8 }}>
                        <LuBuilding2 size={16} /> Benefits at {selectedJob.employer_name}
                      </h2>
                      <div className="mt-2">
                        <SugList items={selBenefits} />
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="stack-3">
                  <div className="job-top">
                    <Skel w={56} h={56} r={14} />
                    <div className="grow stack-2">
                      <Skel w="65%" h={20} />
                      <Skel w="45%" h={13} />
                    </div>
                    <Skel w={76} h={76} r={38} />
                  </div>
                  <Skel h={14} />
                  <Skel h={14} w="85%" />
                  <Skel h={14} w="70%" />
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* ------------ overlays ------------ */}
      <ResumeInsightsSheet
        open={insightsOpen}
        onClose={() => {
          setInsightsOpen(false);
          setAutoStudio(false);
        }}
        job={selectedJob}
        autoStudio={autoStudio}
      />
      <AutoApplySheet open={autoApplyOpen} onClose={() => setAutoApplyOpen(false)} />
      <EasyApplySheet open={easyApplyOpen} onClose={() => setEasyApplyOpen(false)} job={selectedJob} />
      <UpgradeSheet open={upgradeFilters} onClose={() => setUpgradeFilters(false)} text={UPGRADE_COPY.filters} />
    </>
  );
}
